import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  publishTournamentBrackets,
  updateTournamentMatchSchedule,
  updateTournamentMatchStatus,
  updateTournamentParticipantCheckin,
  type AdminTournamentBracketMatch,
  type ParticipantCheckin,
  type AdminTournamentScheduleSlot,
} from "../services/eventRegistrationAdminService";
import { useAdminTournamentDerivedData } from "../hooks/useAdminTournamentDerivedData";
import { useAdminTournamentGeneration } from "../hooks/useAdminTournamentGeneration";
import { useAdminTournamentMatchModals } from "../hooks/useAdminTournamentMatchModals";
import { useAdminTournamentResources } from "../hooks/useAdminTournamentResources";
import AdminTournamentBracketPanel from "./AdminTournamentBracketPanel";
import AdminTournamentOperationsModals from "./AdminTournamentOperationsModals";
import AdminTournamentOperationsHeader from "./AdminTournamentOperationsHeader";
import { AdminTournamentScheduleSettingsPanel } from "./AdminTournamentSettingsPanel";
import AdminTournamentTimelinePanel, {
  type ScheduleEditDraft,
} from "./AdminTournamentTimelinePanel";
import type { MatchCheckInSide } from "./AdminMatchCheckInControl";
import {
  localDateTimeIso,
  slotCourtSelection,
  slotMatchId,
  timelineStatusValue,
  type TournamentOperationCategory,
} from "../utils/tournamentOperations";
import "./AdminTournamentOperationsPanel.css";

type AdminTournamentOperationsPanelProps = {
  activeCategoryKey?: string | null;
  categories: TournamentOperationCategory[];
  onError?: (message: string) => void;
  onPendingChangesChange?: (items: string[]) => void;
  onSuccess?: (message: string) => void;
  scheduleEndAt?: string | null;
  scheduleCourtSettingsHostId?: string;
  scheduleSettingsHostId?: string;
  scheduleStartAt?: string | null;
  token: string;
  tournamentId: number;
  tournamentRegistrationsOpen?: boolean;
  tournamentStarted: boolean;
  tournamentFinished?: boolean;
};

export type AdminTournamentOperationsPanelHandle = {
  savePendingChanges: () => Promise<boolean>;
};

type PanelTab = "brackets" | "timeline";

const AdminTournamentOperationsPanel = forwardRef<
  AdminTournamentOperationsPanelHandle,
  AdminTournamentOperationsPanelProps
>(function AdminTournamentOperationsPanel({
  token,
  tournamentId,
  categories,
  activeCategoryKey,
  onError,
  onPendingChangesChange,
  onSuccess,
  scheduleEndAt,
  scheduleCourtSettingsHostId,
  scheduleSettingsHostId,
  scheduleStartAt,
  tournamentRegistrationsOpen = false,
  tournamentStarted,
  tournamentFinished,
}, ref) {
  const [tab, setTab] = useState<PanelTab>("brackets");
  const [scheduleSettingsHost, setScheduleSettingsHost] =
    useState<HTMLElement | null>(null);
  const [scheduleCourtSettingsHost, setScheduleCourtSettingsHost] =
    useState<HTMLElement | null>(null);
  const [selectedCategoryKey, setSelectedCategoryKey] = useState("");
  const [scheduleEdits, setScheduleEdits] = useState<ScheduleEditDraft>({});
  const [manualGroupCount, setManualGroupCount] = useState("2");
  const [timelineCategoryFilter, setTimelineCategoryFilter] = useState("all");
  const [timelineDayFilter, setTimelineDayFilter] = useState("all");
  const [timelineStatusFilter, setTimelineStatusFilter] = useState("all");
  const [saving, setSaving] = useState(false);
  const [pendingCheckInSideId, setPendingCheckInSideId] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const categoryKey = useMemo(() => {
    const active = String(activeCategoryKey || "").trim();
    const selected = String(selectedCategoryKey || "").trim();
    if (selected) return selected;
    if (active) return active;
    return String(categories[0]?.categoryKey || "").trim();
  }, [activeCategoryKey, categories, selectedCategoryKey]);

  useEffect(() => {
    if (categoryKey) setSelectedCategoryKey(categoryKey);
  }, [categoryKey]);

  const {
    bracket,
    loadingBracket,
    loadingSchedule,
    loadingSettings,
    loadBracket,
    loadManualPairs,
    loadSchedule,
    loadSettings,
    manualAssignments,
    manualPairs,
    schedule,
    scheduleDraft,
    setBracket,
    setManualAssignments,
    setSchedule,
    setScheduleDraft,
    settings,
  } = useAdminTournamentResources({
    categoryKey,
    manualGroupCount,
    onError,
    token,
    tournamentId,
  });

  useEffect(() => {
    void loadSettings();
    void loadBracket();
    void loadManualPairs();
  }, [loadBracket, loadManualPairs, loadSettings]);

  useEffect(() => {
    void loadSchedule();
  }, [loadSchedule]);

  useEffect(() => {
    const timer = window.setInterval(() => setNowMs(Date.now()), 60000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!scheduleSettingsHostId) {
      setScheduleSettingsHost(null);
      return;
    }
    const updateHost = () => {
      setScheduleSettingsHost(document.getElementById(scheduleSettingsHostId));
    };
    updateHost();
    const observer = new MutationObserver(updateHost);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [scheduleSettingsHostId]);

  useEffect(() => {
    if (!scheduleCourtSettingsHostId) {
      setScheduleCourtSettingsHost(null);
      return;
    }
    const updateHost = () => {
      setScheduleCourtSettingsHost(
        document.getElementById(scheduleCourtSettingsHostId),
      );
    };
    updateHost();
    const observer = new MutationObserver(updateHost);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [scheduleCourtSettingsHostId]);

  const {
    bracketMatchById,
    courtNames,
    dayOptions,
    firstKnockoutRoundNumber,
    groupBracketBlocks,
    hasPendingOperationChanges,
    manualGroupOptions,
    roundBracketBlocks,
    scheduleChanged,
    scheduleConfigurationMissing,
    scheduledByMatchId,
    tournamentConfigurationBlocker,
    visibleTimelineSlots,
  } = useAdminTournamentDerivedData({
    bracket,
    categoryKey,
    manualGroupCount,
    schedule,
    scheduleDraft,
    setManualAssignments,
    timelineCategoryFilter,
    timelineDayFilter,
    timelineStatusFilter,
  });

  const pendingOperationChangeItems = useMemo(() => {
    const items: string[] = [];
    if (scheduleChanged) items.push("programação");
    return items;
  }, [scheduleChanged]);

  useEffect(() => {
    onPendingChangesChange?.(pendingOperationChangeItems);
  }, [onPendingChangesChange, pendingOperationChangeItems]);

  const structureGenerationBlocker =
    tournamentRegistrationsOpen
      ? "Encerre as inscrições antes de gerar as chaves."
      : tournamentStarted
        ? "Não é possível gerar chaves depois que o torneio começou."
        : tournamentFinished
          ? "A competição já foi encerrada."
          : tournamentConfigurationBlocker;

  const {
    canEditMatchup,
    matchupModal,
    openMatchupModal,
    openScoreModal,
    saveMatchupModal,
    saveScoreModal,
    scoreModal,
    setMatchupModal,
    setScoreModal,
  } = useAdminTournamentMatchModals({
    bracket,
    categoryKey,
    firstKnockoutRoundNumber,
    loadSchedule,
    manualPairs,
    onError,
    onSuccess,
    setBracket,
    setSaving,
    settings,
    token,
    tournamentId,
  });

  const {
    generateBracket,
    generateTimeline,
    generateManualBracket,
    savePendingOperationChanges,
  } = useAdminTournamentGeneration({
    categoryKey,
    hasPendingOperationChanges,
    loadSchedule,
    manualAssignments,
    manualGroupOptions,
    manualPairs,
    onError,
    onSuccess,
    schedule,
    scheduleChanged,
    scheduleConfigurationMissing,
    scheduleDraft,
    setBracket,
    setSaving,
    setSchedule,
    setScheduleDraft,
    setScheduleEdits,
    token,
    tournamentConfigurationBlocker: structureGenerationBlocker,
    tournamentId,
  });

  useImperativeHandle(
    ref,
    () => ({
      savePendingChanges: savePendingOperationChanges,
    }),
    [savePendingOperationChanges],
  );

  const saveSlotSchedule = async (
    slot: AdminTournamentScheduleSlot,
    edit: ScheduleEditDraft[string],
  ) => {
    const matchId = slotMatchId(slot);
    if (!matchId) {
      onError?.("Esta linha da timeline não está vinculada a uma partida.");
      return;
    }
    const slotStartAt = localDateTimeIso(edit.date, edit.startTime);
    if (!slotStartAt) {
      onError?.("Informe data e horário válidos.");
      return;
    }
    const courtName = String(edit.courtName || "").trim();
    if (!courtName) {
      onError?.("Escolha a quadra para salvar o horário.");
      return;
    }
    const durationDraft = Number(
      edit.durationMinutes || schedule?.config?.defaultMatchDurationMinutes || 60,
    );
    const durationMinutes = Number.isFinite(durationDraft)
      ? Math.max(5, Math.floor(durationDraft))
      : Number(schedule?.config?.defaultMatchDurationMinutes || 60);
    setSaving(true);
    try {
      const nextSchedule = await updateTournamentMatchSchedule(
        token,
        tournamentId,
        matchId,
        {
          slotStartAt,
          courtName,
          durationMinutes,
          allowRoundOrderOverride: true,
          allowSwap: true,
          allowCourtConflict: false,
          lockReason: "manual_override",
        },
      );
      setSchedule(nextSchedule);
      setScheduleEdits((current) => {
        const next = { ...current };
        delete next[String(slot.id)];
        return next;
      });
      await loadSchedule();
      onSuccess?.("Horário atualizado.");
    } catch (error) {
      onError?.(
        error instanceof Error
          ? error.message
          : "Não foi possível atualizar o horário.",
      );
    } finally {
      setSaving(false);
    }
  };

  const startSlotMatch = async (
    slot: AdminTournamentScheduleSlot,
    match: AdminTournamentBracketMatch,
    courtNameOverride?: string,
  ) => {
    const edit = scheduleEdits[String(slot.id)];
    const courtName = String(courtNameOverride ?? edit?.courtName ?? "").trim();
    const matchId = slotMatchId(slot);
    if (!matchId) {
      onError?.("Esta linha da timeline não está vinculada a uma partida.");
      return;
    }
    const currentStatus = timelineStatusValue(slot);
    const nextStatus =
      currentStatus === "in_progress" ? "pending" : "in_progress";
    setSaving(true);
    try {
      if (
        nextStatus === "in_progress" &&
        courtName &&
        courtName !== slotCourtSelection(slot)
      ) {
        const nextSchedule = await updateTournamentMatchSchedule(
          token,
          tournamentId,
          matchId,
          {
            slotStartAt: slot.slotStartAt,
            slotEndAt: slot.slotEndAt,
            courtName,
            durationMinutes: Number(
              schedule?.config?.defaultMatchDurationMinutes || 60,
            ),
            allowRoundOrderOverride: true,
            allowSwap: false,
            allowCourtConflict: true,
            lockReason: "start",
          },
        );
        setSchedule(nextSchedule);
      }
      const next = await updateTournamentMatchStatus(
        token,
        tournamentId,
        String(slot.categoryKey || categoryKey),
        match.id,
        { status: nextStatus },
      );
      if (String(slot.categoryKey || categoryKey) === categoryKey) setBracket(next);
      setScheduleEdits((current) => {
        const nextEdits = { ...current };
        delete nextEdits[String(slot.id)];
        return nextEdits;
      });
      await loadSchedule();
      onSuccess?.(
        nextStatus === "in_progress"
          ? "Jogo iniciado."
          : "Jogo retirado da quadra.",
      );
    } catch (error) {
      onError?.(
        error instanceof Error
          ? error.message
          : "Não foi possível atualizar o status da partida.",
      );
    } finally {
      setSaving(false);
    }
  };

  const normalizeCpf = (value: string | null | undefined) =>
    String(value || "").replace(/\D/g, "").slice(0, 11);

  const replaceCheckins = (list: ParticipantCheckin[]) => {
    setBracket((current) =>
      current ? { ...current, participantCheckins: list } : current,
    );
  };

  const buildOptimisticCheckins = (
    current: ParticipantCheckin[],
    cpfs: string[],
    checkedIn: boolean,
    matchId?: number | null,
  ) => {
    const now = new Date().toISOString();
    const map = new Map<string, ParticipantCheckin>();
    const normalizedMatchId = Number(matchId || 0) || null;
    const keyFor = (cpf: string, itemMatchId?: number | null) => {
      const currentMatchId = Number(itemMatchId || 0);
      return currentMatchId > 0 ? `${currentMatchId}:${cpf}` : cpf;
    };
    current.forEach((item) => {
      const cpf = normalizeCpf(item.cpf);
      if (cpf) map.set(keyFor(cpf, item.matchId), item);
    });
    cpfs.forEach((cpf) => {
      const key = keyFor(cpf, normalizedMatchId);
      if (checkedIn) {
        map.set(key, {
          cpf,
          matchId: normalizedMatchId,
          checkedIn: true,
          checkedInAt: now,
        });
      } else {
        map.delete(key);
      }
    });
    return Array.from(map.values());
  };

  const toggleCheckInSide = async (
    side: MatchCheckInSide,
    checkedIn: boolean,
  ) => {
    const cpfs = Array.from(
      new Set(side.participantCpfs.map(normalizeCpf).filter((cpf) => cpf.length >= 8)),
    );
    if (cpfs.length === 0 || pendingCheckInSideId) return;
    const previous = bracket?.participantCheckins || [];
    const matchId = Number(side.matchId || 0) || null;
    setPendingCheckInSideId(side.id);
    replaceCheckins(buildOptimisticCheckins(previous, cpfs, checkedIn, matchId));
    try {
      let next = previous;
      for (const cpf of cpfs) {
        const response = await updateTournamentParticipantCheckin(
          token,
          tournamentId,
          cpf,
          checkedIn,
          matchId,
        );
        next = response.participantCheckins || [];
      }
      replaceCheckins(next);
    } catch (error) {
      replaceCheckins(previous);
      onError?.(
        error instanceof Error
          ? error.message
          : checkedIn
            ? "Não foi possível confirmar a chegada."
            : "Não foi possível desfazer a chegada.",
      );
    } finally {
      setPendingCheckInSideId(null);
    }
  };

  const publishBrackets = async () => {
    if (!categoryKey || !bracket || saving) return;
    setSaving(true);
    try {
      const result = await publishTournamentBrackets(token, tournamentId);
      const selectedBracket = (result.brackets || []).find(
        (item) => String(item.categoryKey || "") === categoryKey,
      );
      if (selectedBracket) setBracket(selectedBracket);
      await loadSchedule();
      onSuccess?.(
        result.publishedCount === 1
          ? "1 chave publicada para os atletas."
          : `${result.publishedCount} chaves publicadas para os atletas.`,
      );
    } catch (error) {
      onError?.(
        error instanceof Error
          ? error.message
          : "Não foi possível publicar as chaves.",
      );
    } finally {
      setSaving(false);
    }
  };

  const renderScheduleSettings = (section: "courts" | "days") => (
    <AdminTournamentScheduleSettingsPanel
      eventEndAt={scheduleEndAt}
      eventStartAt={scheduleStartAt}
      loadingSettings={loadingSettings || loadingSchedule}
      scheduleDraft={scheduleDraft}
      section={section}
      setScheduleDraft={setScheduleDraft}
    />
  );

  const renderBracket = () => (
    <AdminTournamentBracketPanel
      bracket={bracket}
      canEditMatchup={canEditMatchup}
      canGenerateStructure={
        !tournamentRegistrationsOpen && !tournamentStarted && !tournamentFinished
      }
      categoryKey={categoryKey}
      groupBracketBlocks={groupBracketBlocks}
      loadingBracket={loadingBracket}
      manualAssignments={manualAssignments}
      manualGroupCount={manualGroupCount}
      manualGroupOptions={manualGroupOptions}
      manualPairs={manualPairs}
      onGenerateAutomatic={() => void generateBracket("ranking")}
      onGenerateManual={() => void generateManualBracket()}
      onGenerateTimeline={() => void generateTimeline()}
      onLoadManualPairs={() => void loadManualPairs()}
      onOpenMatchupModal={openMatchupModal}
      onPublish={() => void publishBrackets()}
      roundBracketBlocks={roundBracketBlocks}
      saving={saving}
      scheduledByMatchId={scheduledByMatchId}
      setManualAssignments={setManualAssignments}
      setManualGroupCount={setManualGroupCount}
      tournamentConfigurationBlocker={structureGenerationBlocker}
    />
  );

  const renderTimeline = () => (
    <AdminTournamentTimelinePanel
      bracketMatchById={bracketMatchById}
      categories={categories}
      categoryKey={categoryKey}
      courtNames={courtNames}
      dayOptions={dayOptions}
      loadingSchedule={loadingSchedule}
      nowMs={nowMs}
      onOpenScoreModal={(match, scoreCategoryKey) =>
        void openScoreModal(match, scoreCategoryKey)
      }
      onSaveSlotSchedule={(slot, edit) => void saveSlotSchedule(slot, edit)}
      onToggleCheckInSide={(side, checkedIn) =>
        void toggleCheckInSide(side, checkedIn)
      }
      onStartSlotMatch={(slot, match, courtName) =>
        void startSlotMatch(slot, match, courtName)
      }
      participantCheckins={bracket?.participantCheckins || []}
      pendingCheckInSideId={pendingCheckInSideId}
      saving={saving}
      scheduleEdits={scheduleEdits}
      setScheduleEdits={setScheduleEdits}
      setTimelineCategoryFilter={setTimelineCategoryFilter}
      setTimelineDayFilter={setTimelineDayFilter}
      setTimelineStatusFilter={setTimelineStatusFilter}
      timelineCategoryFilter={timelineCategoryFilter}
      timelineDayFilter={timelineDayFilter}
      timelineStatusFilter={timelineStatusFilter}
      tournamentStarted={tournamentStarted}
      visibleTimelineSlots={visibleTimelineSlots}
    />
  );

  return (
    <section className="admin-tournament-ops">
      {scheduleSettingsHost
        ? createPortal(renderScheduleSettings("days"), scheduleSettingsHost)
        : null}
      {scheduleCourtSettingsHost
        ? createPortal(renderScheduleSettings("courts"), scheduleCourtSettingsHost)
        : null}
      <AdminTournamentOperationsHeader
        categories={categories}
        categoryKey={categoryKey}
        onSelectCategory={setSelectedCategoryKey}
        onSelectTab={setTab}
        tab={tab}
      />

      {tab === "brackets" ? renderBracket() : renderTimeline()}

      <AdminTournamentOperationsModals
        manualPairs={manualPairs}
        matchupModal={matchupModal}
        onCloseMatchup={() => setMatchupModal(null)}
        onCloseScore={() => setScoreModal(null)}
        onSaveMatchup={() => void saveMatchupModal()}
        onSaveScore={() => void saveScoreModal()}
        saving={saving}
        scoreModal={scoreModal}
        setMatchupModal={setMatchupModal}
        setScoreModal={setScoreModal}
      />
    </section>
  );
});

export default AdminTournamentOperationsPanel;
